import { catchError, concatMap, exhaustMap, filter, map, switchMap } from 'rxjs/operators'
import { getType, isActionOf } from 'typesafe-actions'
import { concat, from, of } from 'rxjs'
import { toast } from 'react-toastify'
import { WebAccessTokenPayloadDto } from '@sparling/financial-common'
import {
  getUserRequest,
  getUserSuccess,
  getUserFailure,
  createUserBankAccountRequest,
  createUserBankAccountSuccess,
  createUserBankAccountFailure,
  readUserBankAccountsRequest,
  readUserBankAccountsSuccess,
  readUserBankAccountsFailure,
  updateUserBankAccountRequest,
  updateUserBankAccountSuccess,
  updateUserBankAccountFailure,
  deleteUserBankAccountRequest,
  deleteUserBankAccountSuccess,
  deleteUserBankAccountFailure,
  createUserWalletRequest,
  createUserWalletSuccess,
  createUserWalletFailure,
  readUserWalletsRequest,
  readUserWalletsSuccess,
  readUserWalletsFailure,
  updateUserWalletRequest,
  updateUserWalletSuccess,
  updateUserWalletFailure,
  deleteUserWalletRequest,
  deleteUserWalletSuccess,
  deleteUserWalletFailure,
  readUserSfBankAccountsRequest,
  readUserSfBankAccountsSuccess,
  readUserSfBankAccountsFailure,
  readUserSfWalletsRequest,
  readUserSfWalletsSuccess,
  readUserSfWalletsFailure,
  redirectTo,
  setAuth2FAToken,
  getUserWalletListRequest,
  getUserWalletListSuccess,
  getUserWalletListFailure,
  getClientTypesForAssignToUserFailure,
  getClientTypesForAssignToUserRequest,
  getClientTypesForAssignToUserSuccess,
  userClientTypeRequest,
  userClientTypeSuccess,
  userClientTypeFailure,
  getUserBankAccountListRequest,
  getUserBankAccountListSuccess,
  getUserBankAccountListFailure,
} from '../actions'
import { RootEpic } from '../types'
import { setCookie } from '../../utils'
import { routes } from '../../constants/routes'

export const getUserEpic: RootEpic = (action$, state$, { fetchUser, appContext }) => {
  return action$.pipe(
    filter(isActionOf([getUserRequest])),
    switchMap(({ payload }) =>
      from(fetchUser({ token: payload?.accessToken ?? (state$.value.app.authToken as string) })).pipe(
        concatMap((response) => {
          const updateCtxUserData = (token: string, data: WebAccessTokenPayloadDto) => {
            const ctx = appContext()

            ctx.setAuthTokenInStore(token)
            ctx.setAuth2FAInStore(token)
            ctx.setUserNameInStore(`${data.firstName} ${data.lastName}`)
            ctx.setClientIdInStore(data.clientId)
            ctx.setClientSecretInStore(data.clientHash)
            ctx.setUserIdSfInStore(data.idSf)
            ctx.setUserRoleInStore(data.role)
          }
          const actions = []
          if (payload?.accessToken) {
            const context = appContext()
            const { accessToken } = payload
            if (!payload.after2fa && response.is2faEnabled) {
              context.setAuth2FAInStore(accessToken)
              actions.push(
                of(setAuth2FAToken(accessToken)),
                of(redirectTo(!response?.isTwoFaSecret ? routes.CREATE_TWO_FA : routes.TWO_FA_LOGIN)),
              )
            } else {
              updateCtxUserData(accessToken, response)

              setCookie('token', accessToken)
              toast.success('Login is successful')
            }
            if (payload?.after2fa) context.removeAuth2FAFromStore()
          } else {
            updateCtxUserData(state$.value.app.authToken as string, response)
          }
          actions.push(of(getUserSuccess(response)))
          return concat(...actions)
        }),
        catchError((error) => {
          if (error.response?.status !== 401) {
            toast.error('Failed fetch user.')
          }

          return of(getUserFailure(error.message))
        }),
      ),
    ),
  )
}

export const createUserBankAccountEpic: RootEpic = (action$, state$, { createUserBankAccount }) => {
  return action$.pipe(
    filter(isActionOf([createUserBankAccountRequest])),
    switchMap(({ payload }) =>
      from(
        createUserBankAccount({
          ...payload,
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => {
          toast.success('Successfully created user bank account.')
          return createUserBankAccountSuccess(response)
        }),
        catchError((message) => {
          toast.error('Failed to create user bank account.')
          return of(createUserBankAccountFailure(message))
        }),
      ),
    ),
  )
}

export const fetchUserBankAccountsEpic: RootEpic = (action$, state$, { fetchUserBankAccounts }) => {
  return action$.pipe(
    filter(
      isActionOf([
        getUserSuccess,
        readUserBankAccountsRequest,
        createUserBankAccountSuccess,
        updateUserBankAccountSuccess,
        deleteUserBankAccountSuccess,
      ]),
    ),
    switchMap(({ type, payload }) =>
      from(
        fetchUserBankAccounts({
          ...(type === getType(readUserBankAccountsRequest) && payload),
          ...state$.value.user.bankAccounts.filters,
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => {
          return readUserBankAccountsSuccess(response)
        }),
        catchError((message) => {
          toast.error('Failed to fetch user bank accounts.')
          return of(readUserBankAccountsFailure(message))
        }),
      ),
    ),
  )
}

export const updateUserBankAccountEpic: RootEpic = (action$, state$, { updateUserBankAccount }) => {
  return action$.pipe(
    filter(isActionOf([updateUserBankAccountRequest])),
    switchMap(({ payload }) =>
      from(
        updateUserBankAccount({
          ...payload,
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => {
          toast.success('Successfully updated user bank account.')
          return updateUserBankAccountSuccess(response)
        }),
        catchError((message) => {
          toast.error('Failed to update user bank account.')
          return of(updateUserBankAccountFailure(message))
        }),
      ),
    ),
  )
}

export const deleteUserBankAccountEpic: RootEpic = (action$, state$, { deleteUserBankAccount }) => {
  return action$.pipe(
    filter(isActionOf([deleteUserBankAccountRequest])),
    switchMap(({ payload }) =>
      from(
        deleteUserBankAccount({
          ...payload,
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => {
          toast.success('User bank account deleted successfully.')
          return deleteUserBankAccountSuccess(response)
        }),
        catchError((message) => {
          toast.error('Failed to delete user bank account.')
          return of(deleteUserBankAccountFailure(message))
        }),
      ),
    ),
  )
}

export const createUserWalletEpic: RootEpic = (action$, state$, { createUserWallet }) => {
  return action$.pipe(
    filter(isActionOf([createUserWalletRequest])),
    switchMap(({ payload }) =>
      from(
        createUserWallet({
          ...payload,
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => {
          toast.success('Successfully created user wallet.')
          return createUserWalletSuccess(response)
        }),
        catchError((error) => {
          const message = error.response?.data?.data?.message || error?.message || 'Failed to create user wallet.'
          toast.error(message)
          return of(createUserWalletFailure(message))
        }),
      ),
    ),
  )
}

export const fetchUserWalletsEpic: RootEpic = (action$, state$, { fetchUserWallets }) => {
  return action$.pipe(
    filter(isActionOf([getUserSuccess, readUserWalletsRequest, updateUserWalletSuccess, deleteUserWalletSuccess])),
    switchMap(({ type, payload }) =>
      from(
        fetchUserWallets({
          ...(type === getType(readUserWalletsRequest) && payload),
          ...state$.value.user.wallets.filters,
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => {
          return readUserWalletsSuccess(response)
        }),
        catchError((message) => {
          toast.error('Failed to fetch user wallets.')
          return of(readUserWalletsFailure(message))
        }),
      ),
    ),
  )
}

export const updateUserWalletEpic: RootEpic = (action$, state$, { updateUserWallet }) => {
  return action$.pipe(
    filter(isActionOf([updateUserWalletRequest])),
    switchMap(({ payload }) =>
      from(
        updateUserWallet({
          ...payload,
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => {
          toast.success('Successfully updated user wallet.')
          return updateUserWalletSuccess(response)
        }),
        catchError((message) => {
          toast.error('Failed to update user wallet.')
          return of(updateUserWalletFailure(message))
        }),
      ),
    ),
  )
}

export const deleteUserWalletEpic: RootEpic = (action$, state$, { deleteUserWallet }) => {
  return action$.pipe(
    filter(isActionOf([deleteUserWalletRequest])),
    switchMap(({ payload }) =>
      from(
        deleteUserWallet({
          ...payload,
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => {
          toast.success('User wallet deleted successfully.')
          return deleteUserWalletSuccess(response)
        }),
        catchError((message) => {
          toast.error('Failed to delete user wallet.')
          return of(deleteUserWalletFailure(message))
        }),
      ),
    ),
  )
}

export const fetchUserSfBankAccountsEpic: RootEpic = (action$, state$, { fetchUserSfBankAccounts }) => {
  return action$.pipe(
    filter(isActionOf([readUserSfBankAccountsRequest])),
    switchMap(({ payload }) =>
      from(
        fetchUserSfBankAccounts({
          ...payload,
          count: 10,
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => {
          return readUserSfBankAccountsSuccess(response.data)
        }),
        catchError((message) => {
          toast.error('Failed to fetch sparling bank accounts.')
          return of(readUserSfBankAccountsFailure(message))
        }),
      ),
    ),
  )
}

export const fetchUserSfWalletsEpic: RootEpic = (action$, state$, { fetchUserSfWallets }) => {
  return action$.pipe(
    filter(isActionOf([getUserSuccess, readUserSfWalletsRequest])),
    switchMap(({ payload }) =>
      from(
        fetchUserSfWallets({
          ...payload,
          count: 10,
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => {
          return readUserSfWalletsSuccess(response.data)
        }),
        catchError((message) => {
          toast.error('Failed to fetch sparling wallets.')
          return of(readUserSfWalletsFailure(message))
        }),
      ),
    ),
  )
}

export const fetchUserWalletsListEpic: RootEpic = (action$, state$, { fetchUserWalletsList }) => {
  return action$.pipe(
    filter(isActionOf([getUserWalletListRequest])),
    switchMap(() =>
      from(
        fetchUserWalletsList({
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => {
          return getUserWalletListSuccess(response)
        }),
        catchError((message) => {
          toast.error('Failed to fetch user wallets list.')
          return of(getUserWalletListFailure(message))
        }),
      ),
    ),
  )
}

export const fetchUserBankAccountListEpic: RootEpic = (action$, state$, { fetchUserBankAccountList }) => {
  return action$.pipe(
    filter(isActionOf([getUserBankAccountListRequest])),
    switchMap(() =>
      from(
        fetchUserBankAccountList({
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => {
          return getUserBankAccountListSuccess(response)
        }),
        catchError((message) => {
          toast.error('Failed to fetch user bank account list.')
          return of(getUserBankAccountListFailure(message))
        }),
      ),
    ),
  )
}

export const getClientTypesForAssignToUserEpic: RootEpic = (action$, state$, { fetchClientTypesForAssignToUser }) =>
  action$.pipe(
    filter(isActionOf([getClientTypesForAssignToUserRequest])),
    switchMap(({ payload }) =>
      from(
        fetchClientTypesForAssignToUser({
          token: state$.value.app.authToken as string,
          limit: 10,
          ...payload,
        }),
      ).pipe(
        map((response) => getClientTypesForAssignToUserSuccess(response)),
        catchError((message) => {
          toast.error('Failed to fetch client types.')
          return of(getClientTypesForAssignToUserFailure(message))
        }),
      ),
    ),
  )

export const userClientTypeRequestEpic: RootEpic = (action$, state$, { userClientTypeChangeRequest }) => {
  return action$.pipe(
    filter(isActionOf([userClientTypeRequest])),
    exhaustMap(({ payload }) =>
      from(
        userClientTypeChangeRequest({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        map((response) => {
          if (response.success) toast.success('Request was sent successfully.')
          return userClientTypeSuccess(response)
        }),
        catchError((message) => {
          toast.error('Error Apply to new Client Type.')
          return of(userClientTypeFailure(message))
        }),
      ),
    ),
  )
}

export default [
  getUserEpic,
  createUserBankAccountEpic,
  fetchUserBankAccountsEpic,
  fetchUserSfBankAccountsEpic,
  fetchUserSfWalletsEpic,
  updateUserBankAccountEpic,
  deleteUserBankAccountEpic,
  createUserWalletEpic,
  fetchUserWalletsEpic,
  updateUserWalletEpic,
  deleteUserWalletEpic,
  fetchUserWalletsListEpic,
  getClientTypesForAssignToUserEpic,
  userClientTypeRequestEpic,
  fetchUserBankAccountListEpic,
]
